
























import Component from 'vue-class-component';
import {Mixins, Prop} from 'vue-property-decorator';
import {CodeRetrieveResponse, CommonModelUtils} from '@/model/CommonModel';
import InputMixin from '@/components/input/InputMixin';
import StringUtils from '@/utils/StringUtils';

@Component({
  mixins: [InputMixin]
})
export default class AutocompleteInput extends Mixins<InputMixin>(InputMixin) {
  @Prop({default: 'codeValue'}) private readonly valueName: string;
  @Prop({default: 'codeLabel'}) private readonly labelName: string;
  @Prop({default: ''}) private readonly tooltipName: string;
  @Prop({default: () => []}) private readonly options: any[];
  private focused: boolean = false;

  get innerValue() {
    if (this.focused) {
      return this.value;
    }

    if ((this.options?.length ?? 0) === 0) {
      return this.value;
    }

    // find의 결과가 하나라면 타이핑 한 값이 바로 바인딩 되고 그렇지 않으면 배열이므로 바로 바인딩 되지는 않는다.
    const target = this.options.find(option => option[this.valueName] === this.value);
    return (target && target[this.labelName]) ? target[this.labelName] : this.value;
  }

  set innerValue(value: string) {
    // empty
  }

  get isDisabled() {
    return this.disabled;
  }

  get isExistTooltip() {
    return this.tooltipName;
  }

  public onClear() {
    this.onChangeInput('');
    this.blur();
  }

  public getOptionLabel(option: any) {
    return option[this.labelName] ? `- ${option[this.labelName]}` : option[this.valueName];
  }

  public getOptionTooltip(option: any) {
    return option[this.tooltipName];
  }

  /**
   * 목록에서 선택이 되었을 때 발생하는 이벤트
   */
  public onSelected(option: any) {
    this.commitValue(option[this.valueName]);
    this.$emit('selected', option);

    if (this.changeDetectable) {
      this.$store.commit('commitValue', {
        itemId: this.getId,
        value: option[this.valueName],
        originValue: this.originValue
      });
    }
  }

  /**
   * change 이벤트가 Focus out 시 이벤트 발행이 안되므로 별도로 blur 추가
   * @param $event
   */
  public onBlur($event: any) {
    if (this.isReadOnly) {
      return;
    }

    const target = this.options.find(option => option.codeLabel === this.value);
    if (target) {
      // codeLabel 동일 여부에 대한 검사가 없다면 포커스 후 erase 할 때 제대로 삭제되지 않는 버그가 발생 함.
      // codeValue를 전달하는 경우 onchangeInput의 find 구문에서 코드값이 일치하는 항목이 먼저 튀어나와 바인딩 될 우려가 있음.
      this.onChangeInput(target.codeLabel);
    }
  }

  /**₩
   * Focus out, enter 입력 등으로 인해 입력 폼 값이 변했을 때
   */
  public onChangeInput(data: any) {
    const matchTarget = CommonModelUtils.toLowerCase(data);

    if ((this.options?.length ?? 0) === 0) {
      return;
    }

    const target = this.options.find(
        option => {
          const value = CommonModelUtils.toLowerCase(option[this.valueName]);
          const label = CommonModelUtils.toLowerCase(option[this.labelName]) || CommonModelUtils.toLowerCase(option[this.valueName]);
          return StringUtils.hasValue(matchTarget)
              && ((value && value === matchTarget) || (label && label.indexOf(matchTarget) !== -1));
        });

    if (target) {
      this.commitValue(target[this.valueName]);
      this.$emit('selected', target);
    } else {
      this.commitValue('');
      this.$emit('selected', undefined);
    }

    // clear 후 검색을 하여도 제대로 검색 되지 않는 버그가 있어 강제로 blur 시킨다
    this.blur();
  }

  public onInput(data: any) {
    if (this.isReadOnly || this.isDisabled) {
      return;
    }

    this.focused = true;
    this.$emit('input', data);
  }

  public commitValue(data: any) {
    this.$emit('input', data);
    if (this.changeDetectable) {
      this.$store.commit('commitValue', {
        itemId: this.id,
        value: data,
        originValue: this.originValue
      });
    }
    this.focused = false;
  }

  public blur(delay?: number) {
    const component: any = this.$refs.elementRef;

    if (delay) {
      setTimeout(() => component.$refs.input.$refs.input.blur(), 100);
    } else {
      component.$refs.input.$refs.input.blur();
    }
  }

  public querySearch(queryString: string, callback: any) {
    if (this.isReadOnly || this.isDisabled) {
      callback([]);
      return;
    }

    if ((this.options?.length ?? 0) === 0) {
      callback([]);
      return;
    }

    const find = this.options.find((option: CodeRetrieveResponse) => option.codeLabel === queryString);
    if (find) {
      callback([find]);
      return;
    }

    if (!queryString) {
      callback(this.options);
      return;
    }

    const matchTarget = queryString.toLowerCase();
    callback(
        this.options.filter(option => {
          const value = CommonModelUtils.toLowerCase(option[this.valueName]);
          const label = CommonModelUtils.toLowerCase(option[this.labelName]);

          return (value && value.indexOf(matchTarget) !== -1) || (label && label.indexOf(matchTarget) !== -1);
        })
    );
  }
}
